<template>
  <v-container fluid class="relative header-space container-full">
    <div>
      <h1>Daten bereinigen</h1>

      <v-row v-if="initiated">
        <!-- Left column: Stepper -->
        <v-col md="3" sm="12" class="pr-0">
          <v-stepper class="importStepper" v-model="currentStep" vertical>
            <!-- Step 1: Dry Run -->
            <v-stepper-step step="1" :complete="dryRunResult !== null" class="pl-0">
              Dry Run
            </v-stepper-step>
            <v-stepper-content step="1" class="ml-2 mr-7 pr-2">
              <p class="small grey--text text--darken-2">
                Dry-Run zeigt, welche Daten für dieses Sample gelöscht oder zurückgesetzt würden.
                Es werden dabei <b>keine</b> Änderungen persistiert.
              </p>

              <v-select
                v-model="entityTypeCurrent"
                label="Entitätstyp"
                class="mb-3"
                :items="entityTypeOptions"
                hide-details
              />

              <v-btn
                v-if="!loading"
                color="blue darken-3"
                outlined
                block
                @click="executeDryRun"
                title="Dry-Run ausführen"
              >
                Dry Run ausführen
              </v-btn>
              <v-btn v-else color="blue darken-3" block outlined>
                <v-progress-circular indeterminate color="blue darken-3" :size="25" :width="2"/>
              </v-btn>

              <div v-if="dryRunResult" class="mt-4">
                <p class="small grey--text text--darken-2">Ergebnis (Vorschau):</p>
                <pre class="mono" style="white-space: pre-wrap;">{{ formattedDryRun }}</pre>

                <v-btn
                  class="mt-3"
                  color="blue darken-3"
                  outlined
                  block
                  @click="leaveStepOne"
                >
                  Weiter zur Bestätigung
                </v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 2: Delete -->
            <v-stepper-step step="2" :complete="deleteResult !== null" class="pl-0">
              Endgültig löschen
            </v-stepper-step>
            <v-stepper-content step="2" class="ml-2 mr-7 pr-2">
              <p class="small grey--text text--darken-2">
                Achtung! Diese Aktion ist destruktiv und kann nicht rückgängig gemacht werden.
              </p>

              <v-btn
                v-if="!loading"
                class="error white--text"
                :disabled="!dryRunResult || deleteResult !== null"
                block
                @click="openDeleteConfirm"
                title="Endgültig löschen"
              >
                Endgültig löschen
              </v-btn>
              <v-btn v-else color="blue darken-3" block outlined>
                <v-progress-circular indeterminate color="blue darken-3" :size="25" :width="2"/>
              </v-btn>

              <!-- Delete Confirm Dialog -->
              <v-dialog v-model="confirmDeleteDialog" max-width="450">
                <v-card>
                  <v-card-title>
                    <v-icon large left>mdi-alert-circle-outline</v-icon>
                    <span class="title">Bestätigung erforderlich</span>
                  </v-card-title>
                  <v-card-text>
                    Diese Aktion löscht/entfernt alle ausgewählten Daten für das Sample
                    <span class="mono">{{ sampleId }}</span>. Fortfahren?
                  </v-card-text>
                  <v-card-actions class="px-6 pb-6">
                    <v-btn outlined class="primary--text" @click="confirmDeleteDialog=false">
                      Abbrechen
                    </v-btn>
                    <v-btn class="error" :loading="deleting" :disabled="deleting" @click="confirmDelete">
                      Löschen
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <div v-if="deleteResult" class="mt-4">
                <p class="small grey--text text--darken-2">Ergebnis (Löschung):</p>
                <pre class="mono" style="white-space: pre-wrap;">{{ formattedDelete }}</pre>

                <v-btn class="primary mt-3" block @click="goBackToSamples">
                  Zurück zur Samples-Übersicht
                </v-btn>
              </div>
            </v-stepper-content>
          </v-stepper>
        </v-col>

        <!-- Right column: feedback cards -->
        <v-col md="9" sm="12">
          <div v-if="showFeedback">
            <v-card v-if="loading" class="account-placeholder">
              <h3 class="text-center grey--text">
                <v-progress-circular color="grey" class="mr-2" :width="3" indeterminate />
                <br>
                Daten werden geprüft.
              </h3>
            </v-card>

            <v-card v-if="dryRunIsReadyFeedback && currentStep === 1 && !loading" class="account-placeholder" align="center">
              <h3 class="text-center grey--text">
                <v-icon color="light-green" large>mdi-checkbox-marked-circle-outline</v-icon>
                <br>Der Dry-Run war erfolgreich. <br>Sie können jetzt die Löschung vorbereiten.
              </h3>
              <span class="mb-0 mt-3 small grey--text text--darken-2">
                Vorsicht: Dies ist nur eine Vorschau. Es wurden keine Änderungen persistiert.
              </span><br>
              <v-btn
                color="light-green darken-1"
                text
                class="mr-4 mt-1"
                @click="leaveStepOne"
              >
                Weiter zur Bestätigung
              </v-btn>
            </v-card>
            <v-card
              v-if="deleteIsReady && currentStep === 2 && !loading"
              class="account-placeholder"
            >
              <v-row class="text-center" align="center" justify="center">
                <v-col cols="12">
                  <h3 class="grey--text">
                    <v-icon color="light-green" large>mdi-checkbox-marked-circle-outline</v-icon>
                    <br>
                    Die Daten wurden erfolgreich gelöscht/zurückgesetzt.
                  </h3>
                </v-col>

                <v-col cols="12">
                <span class="small grey--text text--darken-2">
                  Hinweis: Damit Sie dieses Sample löschen können, müssen Sie es jetzt ablehnen.
                 </span>
                </v-col>
                <v-col cols="12">
                  <v-btn
                    color="light-green darken-1"
                    class="mt-2"
                    text
                    @click="goBackToSamples"
                  >
                    Zur Samples-Übersicht
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
export default {
  name: 'SampleDeleteDescendants',
  props: {
    sampleId: { type: String, required: true }
  },
  data: () => ({
    initiated: false,
    currentStep: 1,
    loading: false,
    deleting: false,
    dryRunResult: null,
    deleteResult: null,
    confirmDeleteDialog: false,
    entityTypeOptions: ['all', 'change', 'award', 'score', 'product', 'account', 'contact'],
    entityTypeCurrent: 'all',
    showFeedback: true
  }),
  computed: {
    dryRunIsReadyFeedback () {
      return !!this.dryRunResult && !this.loading
    },
    deleteIsReady () {
      return !!this.deleteResult && !this.loading
    },
    formattedDryRun () {
      return this.dryRunResult ? JSON.stringify(this.dryRunResult, null, 2) : ''
    },
    formattedDelete () {
      return this.deleteResult ? JSON.stringify(this.deleteResult, null, 2) : ''
    }
  },
  methods: {
    async executeDryRun () {
      this.showFeedback = true
      this.loading = true
      this.dryRunResult = null
      const res = await this.$http.delete(`/sample/delete-descendants/${this.sampleId}/${this.entityTypeCurrent}/true`, {
        data: { dryrun: true, type: this.entityTypeCurrent }
      })
      this.dryRunResult = res?.data || { dryrun: true }
      this.loading = false
    },
    leaveStepOne () {
      this.currentStep = 2
      this.showFeedback = true
    },
    openDeleteConfirm () {
      this.confirmDeleteDialog = true
    },
    async confirmDelete () {
      this.deleting = true
      this.confirmDeleteDialog = false
      await this.executeDelete()
      this.deleting = false
    },
    async executeDelete () {
      this.loading = true
      this.deleteResult = null
      const res = await this.$http.delete(`/sample/delete-descendants/${this.sampleId}/${this.entityTypeCurrent}/0`, {
        data: { dryrun: false, type: this.entityTypeCurrent }
      })
      this.deleteResult = res?.data || { message: 'Löschung ausgeführt.' }
      this.loading = false
    },
    goBackToSamples () {
      this.$router.push('/verwaltung/samples')
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      await this.$router.push('/')
      return
    }
    await this.executeDryRun()
    this.initiated = true
  }
}
</script>

<style scoped>
.account-placeholder {
  height: 300px;
  padding-top: 110px;
}
.v-data-table td {
  padding-top: 10px;
}
.mono {
  font-family: monospace;
  font-size: 13px;
}
</style>
